// src/components/HanziCharacter.js (or CmnStrokeUtils.js)

import React, { useEffect, useRef, useState } from 'react';
import HanziWriter from 'hanzi-writer';
import './CmnStrokeUtilsType.css';

const HanziCharacter = ({ character, prefixpath = '' }) => {
  const baseHostUrl = process.env.NODE_ENV === 'production' ? 'https://growlemma.com' : 'http://localhost:80';

  const targetRef = useRef(null);
  const writerRef = useRef(null);
  const [isQuizzing, setIsQuizzing] = useState(false);
  const difficultySettings = {
  easy: {
    leniency: 1.5,
    showHintAfterMisses: 1,
  },
  medium: {
    leniency: 1.0,
    showHintAfterMisses: 2,
  },
  hard: {
    leniency: 0.7,
    showHintAfterMisses: 3,
  },
};
  // useEffect to create the HanziWriter instance. This runs when the character changes.
  useEffect(() => {
    // Ensure we have a character and a target element
    if (character && targetRef.current) {
      // Clear any previous instance
      targetRef.current.innerHTML = '';
      writerRef.current = null;

      const writer = HanziWriter.create(targetRef.current, character.charAt(0), {
        width: 100,
        height: 100,
        padding: 5,
        // Initial state should not be in quiz mode
        showCharacter: true,
        showOutline: false,
        // Standard animation options
        strokeAnimationSpeed: 0.5,
        delayBetweenStrokes: 200,
        radicalColor: '#FF0000',
        // Your custom data loader
        charDataLoader: function(char, onComplete) {
          if (!char) {
            alert("No headword available!");
            return;
          }
          const encodedChar = encodeURIComponent(char);
          const url = `${baseHostUrl}/hanzi-writer-data/${encodedChar}.json`;
          fetch(url, { credentials: 'include' })
            .then(response => {
              if (!response.ok) {
                throw new Error(`Failed to load character data for ${char}`);
              }
              return response.json();
            })
            .then(data => onComplete(data))
            .catch(err => console.error(err));
        }
      });
      writerRef.current = writer;
    }

    // When the character prop changes, ensure we are not in quiz mode.
    setIsQuizzing(false);

    // Cleanup on component unmount
    return () => {
      writerRef.current = null;
    };
  }, [character]);


  // --- THIS IS THE CORRECTED useEffect FOR MANAGING THE QUIZ LIFECYCLE ---
  useEffect(() => {
    const writer = writerRef.current;
    if (!writer) return;

    // This effect runs whenever `isQuizzing` state changes.
    if (isQuizzing) {
      const currentSettings = difficultySettings.easy;
      // If we are entering quiz mode, start the quiz.
      writer.quiz({
        ...currentSettings,
        onCorrectStroke: (data) => {
          //console.log('Correct!', data)
        },
        onMistake: (data) => {
          //console.log('Mistake!', data)
        },
        onQuizComplete: (summary) => {
          //console.log('Quiz Complete!', summary);
          alert(`Quiz Complete! Total mistakes: ${summary.totalMistakes}`);
          setIsQuizzing(false); // Automatically exit quiz mode on completion
        },
      });

      // The return function from useEffect is a CLEANUP function.
      // It runs when the component unmounts OR when the dependencies (isQuizzing) change.
      // So, when isQuizzing goes from `true` to `false`, this cleanup will run.
      return () => {
        // Use the official API method to cancel the quiz
        writer.cancelQuiz();
        // After cancelling, we must explicitly show the character again.
        writer.showCharacter();
      };
    }
  }, [isQuizzing]);


  // --- Button Handlers ---
  const handleAnimate = () => {
    if (writerRef.current && !isQuizzing) {
      writerRef.current.animateCharacter();
    }
  };

  const handleQuiz = () => {
    // The button's only job is to toggle the state.
    // The useEffect hook will handle the consequences of this state change.
    setIsQuizzing(current => !current);
  };

  return (
    <div className="stroke-container">
      {/* The class is added to change the cursor during the quiz */}
      <div
        ref={targetRef}
        className={`cmn-stroke-elm ${isQuizzing ? 'quiz-active' : ''}`}
      ></div>

      <div className="button-column">
        <button onClick={handleAnimate} disabled={isQuizzing} className="cmn-stroke-btn">
          Animate
        </button>
        <button onClick={handleQuiz} className="cmn-stroke-btn quiz-btn">
          {isQuizzing ? 'Cancel Quiz' : 'Quiz'}
        </button>
      </div>
    </div>
  );
};

export default HanziCharacter;